/**
 * Admin V3 — Financial / Ledger — Table component
 *
 * 7 columns: (expand) | Date / Heure | Type (pill) | Chauffeur | Compte
 *           | Montant | Référence.
 *
 * Per-row expandable detail panel shows:
 *   - entry_id, tenant_id, reason, created_by
 *   - Derived balances after entry (pending / available / total) with a
 *     visible "Soldes après entrée (dérivés)" header reinforcing the
 *     ledger-derived nature of these numbers.
 *   - operation_id, cycle_id, eligible_for_release_at, frozen flag
 *   - Cross-reference IDs (released_operation_id, reversed_operation_id,
 *     failed_operation_id)
 *
 * The external provider field `stripe_transfer_id` is DELIBERATELY OMITTED
 * from display (consistent with broader provider-identifier policy).
 *
 * Chauffeur cell links to /admin-v3/drivers/:id when driver_id present.
 * Référence cell links to /admin/bookings/:id when booking_id present.
 *
 * NO mutation surface.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import { BookOpen, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink, Snowflake } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  ledgerEntryTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const truncate = (s, n = 12) => {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
};

// Defensive type resolution: prefer canonical operation_type over legacy entry_type.
const resolveTypeMeta = (entry) => {
  const key = entry?.operation_type || entry?.entry_type || null;
  if (!key) return { label: '—', pill: 'bg-slate-100 text-slate-500 border border-slate-200', _key: null };
  const meta = ledgerEntryTypeMeta[key];
  if (meta) return { ...meta, _key: key };
  return { label: key, pill: 'bg-slate-100 text-slate-500 border border-slate-200', _key: key };
};

// Account label resolution: prefer canonical `account` field, fall back to
// affects_* booleans (legacy schema).
const resolveAccount = (entry, locale) => {
  if (entry?.account === 'pending')   return t('financial.ledger.account.pending',   locale);
  if (entry?.account === 'available') return t('financial.ledger.account.available', locale);
  const ap = Boolean(entry?.affects_pending);
  const aa = Boolean(entry?.affects_available);
  if (ap && aa) return t('financial.ledger.account.both',      locale);
  if (ap)       return t('financial.ledger.account.pending',   locale);
  if (aa)       return t('financial.ledger.account.available', locale);
  return t('financial.ledger.account.none', locale);
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(7).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ entry, locale }) => {
  const meta = [
    entry.entry_id              && [t('financial.ledger.detail.entryId',     locale), entry.entry_id],
    entry.tenant_id             && [t('financial.ledger.detail.tenant',      locale), entry.tenant_id],
    entry.reason                && [t('financial.ledger.detail.reason',      locale), entry.reason],
    entry.created_by            && [t('financial.ledger.detail.createdBy',   locale), entry.created_by],
    entry.operation_id          && [t('financial.ledger.detail.operationId', locale), entry.operation_id],
    entry.cycle_id              && [t('financial.ledger.detail.cycleId',     locale), entry.cycle_id],
    entry.eligible_for_release_at && [t('financial.ledger.detail.eligibleAt', locale), formatTs(entry.eligible_for_release_at, locale)],
    entry.released_operation_id && [t('financial.ledger.detail.releasedFrom', locale), entry.released_operation_id],
    entry.reversed_operation_id && [t('financial.ledger.detail.reversedFrom', locale), entry.reversed_operation_id],
    entry.failed_operation_id   && [t('financial.ledger.detail.failedFrom',  locale), entry.failed_operation_id],
  ].filter(Boolean);

  return (
    <div className="space-y-3 bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      {entry.frozen && (
        <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
          <Snowflake className="h-3 w-3" />
          {t('financial.ledger.detail.frozen', locale)}
        </div>
      )}

      {meta.length > 0 && (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          {meta.map(([label, value]) => (
            <div key={label} className="text-[12px]">
              <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
              <dd className="text-slate-700 font-mono break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {(entry.pending_balance_after != null
        || entry.available_balance_after != null
        || entry.total_balance_after != null) && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
            {t('financial.ledger.detail.balances', locale)}
          </div>
          <dl className="grid grid-cols-3 gap-2">
            <div className="text-[12px]">
              <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                {t('financial.ledger.detail.balancePending', locale)}
              </dt>
              <dd className="text-slate-700 font-medium tabular-nums">{currency(entry.pending_balance_after ?? 0)}</dd>
            </div>
            <div className="text-[12px]">
              <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                {t('financial.ledger.detail.balanceAvailable', locale)}
              </dt>
              <dd className="text-slate-700 font-medium tabular-nums">{currency(entry.available_balance_after ?? 0)}</dd>
            </div>
            <div className="text-[12px]">
              <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                {t('financial.ledger.detail.balanceTotal', locale)}
              </dt>
              <dd className="text-slate-900 font-bold tabular-nums">{currency(entry.total_balance_after ?? 0)}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
};

const FinancialLedgerTable = ({ entries, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-financial-ledger-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('financial.ledger.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-financial-ledger-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('financial.ledger.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={BookOpen}
      title={t('financial.ledger.empty.title', locale)}
      desc={t(isFilterActive ? 'financial.ledger.empty.descFilter' : 'financial.ledger.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('financial.ledger.col.timestamp', locale),
                t('financial.ledger.col.type',      locale),
                t('financial.ledger.col.driver',    locale),
                t('financial.ledger.col.account',   locale),
                t('financial.ledger.col.amount',    locale),
                t('financial.ledger.col.reference', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              entries.map((entry, idx) => {
                const key = entry.entry_id || `${entry.created_at}-${entry.driver_id}-${idx}`;
                const isExpanded = Boolean(expanded[key]);
                const typeMeta = resolveTypeMeta(entry);
                const accountLabel = resolveAccount(entry, locale);
                const amount = Number(entry.amount) || 0;
                const isNegative = amount < 0;
                const driverPath = entry.driver_id ? `/admin/drivers/${entry.driver_id}` : null;
                const bookingPath = entry.booking_id ? bookingDetailPath(entry.booking_id) : null;

                return (
                  <React.Fragment key={key}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded ? t('financial.ledger.detail.hide', locale) : t('financial.ledger.detail.show', locale)}
                          data-testid={`adminv3-financial-ledger-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Date / Heure */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(entry.created_at, locale)}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={typeMeta} />
                      </td>

                      {/* Chauffeur */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {driverPath ? (
                          <Link
                            to={driverPath}
                            className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`adminv3-financial-ledger-driver-${key}`}
                          >
                            {truncate(entry.driver_id, 12)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Compte */}
                      <td className="px-4 py-3.5 text-slate-700 text-[12px]">
                        {accountLabel}
                      </td>

                      {/* Montant (signed) */}
                      <td className={cn(
                        'px-4 py-3.5 font-bold whitespace-nowrap tabular-nums',
                        isNegative ? 'text-rose-700' : amount > 0 ? 'text-emerald-700' : 'text-slate-400'
                      )}>
                        {isNegative ? '−' : amount > 0 ? '+' : ''}{currency(Math.abs(amount))}
                      </td>

                      {/* Référence (booking_id) */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {bookingPath ? (
                          <Link
                            to={bookingPath}
                            className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`adminv3-financial-ledger-booking-${key}`}
                          >
                            {truncate(entry.booking_id, 12)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={7} className="px-4 py-2">
                          <DetailPanel entry={entry} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default FinancialLedgerTable;
